import React, { useEffect, useState, useContext, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Header from "../../header";
import { Footer2 } from "../../footer2";
import InstructorSidebar from "../sidebar";
import { getAuthorDetail } from "../../../services/authorService";
import { getauthorstemplatesandattributedatasearch } from "../../../services/authorstemplatesandattributeService";
import { AppContext } from "../../../services/AuthContext";
import { extractIdFromSlug, buildAuthorPath } from "../../../utils/slugify";
import usePageTracking from "../../../hooks/usePageTracking";

const Field = ({ label, value, href }) => {
  if (!value) return null; // <-- boşsa hiç gösterme
  return (
    <div className="col-sm-6">
      <div className="text-sm text-newdarkfume my-2">
        <h6>{label}</h6>
        {href ? (
          <p><a href={href} target="_blank" rel="noopener noreferrer">{value}</a></p>
        ) : (
          <p className="text-darkfume">{value}</p>
        )}
      </div>
    </div>
  );
};


// boş HTML / metin kontrolü
const stripHtml = (s = "") => s.replace(/<[^>]+>/g, "").replace(/\s+/g, "").trim();
const hasHtmlContent = (html = "") => stripHtml(html) !== "";

// attributes için label & value çıkar
const normalizeAttr = (attribute) => {
  const attrName =
    attribute?.authordata?.attributedata?.templatetypeandattribute?.attribute?.attrname || "-";
  const values = attribute?.datajson?.[0]?.value || [];
  const valueStr = Array.isArray(values) ? values.filter(Boolean).join(", ") : "";
  return { name: attrName, value: valueStr };
};


const AuthorsDashboardProfile = () => {
  usePageTracking("author");
  const { slug } = useParams();
  const navigate = useNavigate();
  const { auth } = useContext(AppContext);

  // id → stabilize et
  const idNum = useMemo(() => {
    const raw = extractIdFromSlug(slug);
    return raw && /^\d+$/.test(raw) ? Number(raw) : null;
  }, [slug]);

  const [profileData, setProfileData] = useState(null);
  const [attributesData, setAttributesData] = useState([]);

  // 1) Author fetch
  useEffect(() => {
    if (!idNum) { navigate("/home", { replace: true }); return; }
    let alive = true;

    (async () => {
      try {
        const resp = await getAuthorDetail(idNum, auth?.token);
        if (!alive) return;
        if (resp?.status !== "OK" || !resp?.data) { navigate("/home", { replace: true }); return; }
        setProfileData(resp.data); // ✅ SADECE set et; null’a geri dönme
      } catch {
        navigate("/home", { replace: true });
      }
    })();

    return () => { alive = false; };
  }, [idNum, auth?.token, navigate]);

  // 2) Canonical check (sadece 1 kez yönlendir)
  const didCanonicalFixRef = useRef(false);
  useEffect(() => {
    if (!profileData || !idNum || didCanonicalFixRef.current) return;
    const want = buildAuthorPath({ name: profileData.name, surname: profileData.surname, id: idNum }).replace(/^\/author\//, "");
    if (slug !== want) {
      didCanonicalFixRef.current = true;
      navigate(`/author/${want}`, { replace: true });
    }
  }, [profileData, idNum, slug, navigate]);

  // 3) Attributes fetch → profil sabitlendikten sonra
  useEffect(() => {
    if (!idNum || !profileData) return;
    let alive = true;
    (async () => {
      try {
        const attrs = await getauthorstemplatesandattributedatasearch(idNum, 1, auth?.token);
        if (!alive) return;
        if (attrs?.status === "OK") setAttributesData(attrs.data || []);
      } catch {
        //ignore
      }
    })();
    return () => { alive = false; };
  }, [idNum, profileData, auth?.token]);

  // DEBUG: render akışı
  console.log("render", { slug, idNum, hasProfile: !!profileData });

  if (!profileData) return <p>Yükleniyor...</p>;

  // Field bileşeni aynı kalsın

  // ⬇️ yardımcılar (dosyanın üstüne ekle veya mevcutların altına koy)
  const isEmpty = (v) => v == null || (typeof v === "string" && v.trim() === "");

  // ... component içinde, render’dan hemen önce:
  const hasAnyInfo =
    !isEmpty(profileData.orcid) ||
    !isEmpty(profileData.yokid) ||
    !isEmpty(profileData.institution) ||
    !isEmpty(profileData.faculty) ||
    !isEmpty(profileData.department);


  const fullName = `${profileData.title ? profileData.title + " " : ""}${profileData.name} ${profileData.surname}`.trim();
  const canonical = window.location.origin + buildAuthorPath({ name: profileData.name, surname: profileData.surname, id: idNum });
  const sameAs = profileData.orcid ? [`https://orcid.org/${profileData.orcid}`] : [];
  const personLd = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: fullName,
    affiliation: profileData.institution || undefined,
    identifier: profileData.yokid || undefined,
    sameAs: sameAs.length ? sameAs : undefined,
    url: canonical
  };

  return (
    <div className="main-wrapper mt-5">
      <Helmet>
        <title>{`${fullName} | Author`}</title>
        <link rel="canonical" href={canonical} />
        <meta name="description" content={`${fullName} hakkında detaylar, yayınlar ve kurum bilgisi.`} />
        <meta property="og:type" content="profile" />
        <meta property="og:title" content={fullName} />
        <meta property="og:url" content={canonical} />
        <script type="application/ld+json">{JSON.stringify(personLd)}</script>
      </Helmet>

      <Header />
      {/* Breadcrumb */}
      <div className="container">
        <div className="row">
        </div>
      </div>
      {/* /Breadcrumb */}
      {/* Page Content */}
      <div className="page-content">
        <div className="container">
          <div className="row">
            {/* Sidebar */}
            <InstructorSidebar author={profileData} authorId={idNum} />
            {/* /Sidebar */}
            {/* Instructor Profile */}
            <div className="col-xl-9 col-lg-9">
              <div className="settings-widget card-details mb-0">
                <div className="settings-menu p-0">
                  <div className="profile-heading">
                    <h3>{`${profileData.title} ${profileData.name} ${profileData.surname}`}</h3>
                  </div>
                  <div className="checkout-form personal-address">
                    <div className="row">
                      {hasAnyInfo ? (
                        <>
                          <Field
                            label="ORCID"
                            value={profileData.orcid}
                            href={profileData.orcid ? `https://orcid.org/${profileData.orcid}` : undefined}
                          />
                          <Field label="YÖK ID" value={profileData.yokid} />
                          <Field label="Kurum" value={profileData.institution} />
                          <Field label="Fakülte" value={profileData.faculty} />
                          <Field label="Bölüm" value={profileData.department} />
                        </>
                      ) : (
                        <div className="col-sm-12">
                          <p className="text-muted mb-0">Yazara ait bilgi bulunmamaktadır.</p>
                        </div>
                      )}
                    </div>
                  </div>

                </div>
              </div>

              {/* Ek Bilgiler */}
              <div className="settings-widget card-details mt-3">
                <div className="settings-menu p-0">
                  <div className="profile-heading">
                    <h3>Ek Bilgiler</h3>
                  </div>
                  <div className="checkout-form personal-address">
                    <div className="row">
                      {(() => {
                        // sadece şu alanlar ve DOLU olanlar
                        const wanted = ["alan", "uzmanlık alanları", "ülke", "uyruk"];
                        const normalized = (attributesData || [])
                          .filter((a) => {
                            const n = a?.authordata?.attributedata?.templatetypeandattribute?.attribute?.attrname?.toLowerCase();
                            return wanted.includes(n);
                          })
                          .map(normalizeAttr)
                          .filter((x) => x.value && x.value.trim() !== "");

                        if (normalized.length === 0) {
                          return (
                            <div className="col-sm-12">
                              <p className="text-muted mb-0">Yazara ait bilgi bulunmamaktadır.</p>
                            </div>
                          );
                        }

                        return normalized.map((item, idx) => (
                          <div className="col-sm-12 mb-3" key={idx}>
                            <div className="text-sm text-newdarkfume">
                              <h6>{item.name}</h6>
                              <p className="text-fume">{item.value}</p>
                            </div>
                          </div>
                        ));
                      })()}
                    </div>
                  </div>
                </div>
              </div>

            </div>
            {/* /Instructor Profile */}
          </div>
        </div>
      </div>
      {/* /Page Content */}
      <Footer2 />
    </div>
  );
};

export default AuthorsDashboardProfile;
